"use client";

import { useEffect, useState } from "react";
import { ArrowRight, Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetHeader, SheetClose } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-6">
        <div className="h-4 w-16 bg-muted animate-pulse rounded-md" data-index={0} />
        <div className="h-4 w-20 bg-muted animate-pulse rounded-md" data-index={1} />
        <div className="h-4 w-16 bg-muted animate-pulse rounded-md" data-index={2} />
        <div className="h-4 w-20 bg-muted animate-pulse rounded-md" data-index={3} />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-2 lg:gap-4">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative inline-flex items-center justify-center px-3 py-2 text-[16px] font-default font-bold tracking-normal uppercase text-foreground hover:text-primary focus:text-primary transition-colors duration-300">
                <span className="relative">
                  {item.label}
                </span>
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 mt-12">
        <div className="h-12 w-3/4 bg-muted animate-pulse rounded-md" data-index={0} />
        <div className="h-12 w-2/3 bg-muted animate-pulse rounded-md" data-index={1} />
        <div className="h-12 w-3/4 bg-muted animate-pulse rounded-md" data-index={2} />
      </div>;
  }
  return <nav className="flex flex-col gap-2 mt-8">
      {menuItems.map((item, i) => <SheetClose asChild key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="group block py-3 font-default text-4xl font-bold tracking-tight text-foreground hover:text-primary transition-colors duration-500">
            <span className="inline-flex items-baseline gap-3">
              {item.label}
            </span>
          </Link>
        </SheetClose>)}
    </nav>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return <section data-section-id="4581" id="header-principal" className={`sticky top-0 z-50 w-full transition-all duration-500 ease-out ${scrolled ? "bg-background/95 backdrop-blur-sm border-b border-border" : "bg-background border-b border-transparent"}`}>
      <div className="mx-auto w-full max-w-7xl px-6 sm:px-8">
        <div className={`flex items-center justify-between transition-all duration-500 ${scrolled ? "h-20" : "h-36"}`}>
          <div className="flex items-center shrink-0">
            <Link to="/" aria-label="Formexa Construction LTD — Home" className="inline-flex items-center">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1719873/img/dd752db1-0167-47b4-b582-2e273b41bed7.jpg" alt="Formexa Construction LTD Logo" className="h-24 !w-[200px]" loading="lazy" src="https://genuine-manatee.10web.cloud/wp-content/uploads/2026/06/38d17028-cda6-4805-8351-f322a7c1cfe9.jpg" />
            </Link>
          </div>

          <div className="hidden md:flex flex-1 items-center justify-end px-4">
            <MenuProvider menu_id="16">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          <div className="md:hidden flex items-center">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="sm" className="h-10 px-3 text-base text-foreground hover:bg-transparent focus:bg-transparent gap-2" aria-label="Abrir menú">
                  <Menu className="size-6" />
                  <span className="font-default text-base font-bold tracking-tight">
                    Menú
                  </span>
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:max-w-md bg-background border-l border-border p-0">
                <SheetHeader className="px-6 pt-6 pb-2 flex flex-row items-center justify-between space-y-0">
                  <SheetTitle asChild>
                    <Link to="/" onClick={() => setMobileOpen(false)}>
                      <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1719873/img/dd752db1-0167-47b4-b582-2e273b41bed7.jpg" alt="Formexa Construction LTD Logo" className="h-16 w-auto" loading="lazy" />
                    </Link>
                  </SheetTitle>
                  <SheetClose asChild>
                    <Button variant="ghost" size="icon" className="h-9 w-9 hover:bg-accent" aria-label="Cerrar menú">
                      <X className="size-6" />
                    </Button>
                  </SheetClose>
                </SheetHeader>

                <div className="flex flex-col h-[calc(100%-72px)] px-6 pb-8 overflow-y-auto">
                  <MenuProvider menu_id="16">
                    <MobileMenuConsumer />
                  </MenuProvider>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </section>;
}